import { createRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { authenticatedRoute } from '@/authenticated-route';
import { SdrsPatientCardSource } from '@/features/delivery-room/components/patient-card-source';
import { getSuspenseQueryPatientById } from '@/features/delivery-room/services/use-query-patient-by-id';
import {
  assessmentFormsSearchSchema,
  deliveryRoomSearchParamsSchema,
} from '@/features/delivery-room/types/delivery-room-search-params';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { DELIVERY_ROOM_ROUTES } from './consts/routes';

const MATERNITY_PROFILE_STATIC_DATA = {
  parent: DELIVERY_ROOM_ROUTES.patientList,
  home: DELIVERY_ROOM_ROUTES.index,
  patientCardSource: SdrsPatientCardSource,
} as const;

const getParentRoute = () => authenticatedRoute;

const deliveryRoomPatientListRoute = createRoute({
  getParentRoute,
  path: DELIVERY_ROOM_ROUTES.patientList,
  validateSearch: zodValidator(deliveryRoomSearchParamsSchema),
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'deliveryRoomEnabled',
  }),
  staticData: {
    patientCardSource: SdrsPatientCardSource,
  },
}).lazy(() =>
  import('./delivery-room-patient-list').then(({ Route }) => Route),
);

const maternalPatientProfileRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: DELIVERY_ROOM_ROUTES.maternityProfile,
  loader: async ({ params: { id }, context: { queryClient } }) => {
    const { encounterId, mrn, laborStage } = await queryClient.ensureQueryData(
      getSuspenseQueryPatientById(id),
    );

    return {
      laborStage,
      encounterId,
      mrn,
    };
  },
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'deliveryRoomEnabled',
  }),
  staticData: MATERNITY_PROFILE_STATIC_DATA,
}).lazy(() => import('./maternity-profile').then(({ Route }) => Route));

const laborRecordsRoute = createRoute({
  getParentRoute: () => maternalPatientProfileRoute,
  path: '/',
  staticData: MATERNITY_PROFILE_STATIC_DATA,
}).lazy(() => import('./labor-records').then(({ Route }) => Route));

const assessmentFormsRoute = createRoute({
  getParentRoute: () => maternalPatientProfileRoute,
  path: 'assessment-forms',
  staticData: MATERNITY_PROFILE_STATIC_DATA,
  validateSearch: zodValidator(assessmentFormsSearchSchema),
}).lazy(() => import('./assessment-forms').then(({ Route }) => Route));

export const deliveryRoomRoute = createRoute({
  getParentRoute,
  path: DELIVERY_ROOM_ROUTES.index,
  beforeLoad: async (context) => {
    await beforeLoadRouterGuard([], {
      featureFlag: 'deliveryRoomEnabled',
    })(context);
    throw redirect({
      to: DELIVERY_ROOM_ROUTES.patientList,
    });
  },
}).addChildren([
  deliveryRoomPatientListRoute,
  maternalPatientProfileRoute.addChildren([
    laborRecordsRoute,
    assessmentFormsRoute,
  ]),
]);
